/**
 * Google Maps JavaScript API 加载器
 * 动态注入 script 加载官方 SDK，避免引入 npm 依赖；全局共享同一个加载 Promise
 * TODO: 填入真实的 Google Maps API Key（需在 Google Cloud 开通 Maps JavaScript API）
 */
// 显式 string 标注：避免 const 字面量收窄导致 isGoogleMapsKeyConfigured 中 !== '' 触发 TS2367（key 会随构建替换，值不定）
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
const GOOGLE_MAPS_API_KEY: string = 'AIzaSyD0pur2I7xoorGd9uF85VtdWCsRNhoZ5r0'

/** 地图实例（仅包含本模块使用到的 API 子集，零依赖类型声明） */
export interface GoogleMapsLatLngBounds {
  extend(point: unknown): void
}

export interface GoogleMapsMap {
  setCenter(center: unknown): void
  setZoom(zoom: number): void
  fitBounds(bounds: GoogleMapsLatLngBounds): void
}

export interface GoogleMapsMarker {
  setMap(map: GoogleMapsMap | null): void
  addListener(eventName: string, handler: () => void): unknown
}

export interface GoogleMapsPolyline {
  setMap(map: GoogleMapsMap | null): void
}

export interface GoogleMapsInfoWindow {
  open(map: GoogleMapsMap | null, anchor?: GoogleMapsMarker): void
  close(): void
}

export interface GoogleMapsSdk {
  Map: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMapsMap
  Marker: new (options: Record<string, unknown>) => GoogleMapsMarker
  Polyline: new (options: Record<string, unknown>) => GoogleMapsPolyline
  LatLng: new (lat: number, lng: number) => unknown
  LatLngBounds: new () => GoogleMapsLatLngBounds
  InfoWindow: new (options: Record<string, unknown>) => GoogleMapsInfoWindow
  Size: new (width: number, height: number) => unknown
  Point: new (x: number, y: number) => unknown
  /** 容器从 display:none 恢复显示后触发 resize 重算尺寸 */
  event?: {
    trigger(instance: unknown, eventName: string): void
  }
}

declare global {
  interface Window {
    google?: { maps?: GoogleMapsSdk }
    __initGoogleMaps?: () => void
  }
}

let loadingPromise: Promise<GoogleMapsSdk> | null = null

/** 是否已配置 API Key（未配置时地图功能展示提示，不发请求） */
export function isGoogleMapsKeyConfigured(): boolean {
  return GOOGLE_MAPS_API_KEY !== ''
}

/** 加载 Google Maps SDK（重复调用共享同一 Promise，SDK 已加载时直接返回） */
export function loadGoogleMaps(): Promise<GoogleMapsSdk> {
  if (window.google?.maps) {
    return Promise.resolve(window.google.maps)
  }
  if (loadingPromise) {
    return loadingPromise
  }
  loadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&callback=__initGoogleMaps`
    script.async = true
    script.onerror = () => {
      loadingPromise = null
      reject(new Error('Google Maps SDK 加载失败'))
    }
    window.__initGoogleMaps = () => {
      if (window.google?.maps) {
        resolve(window.google.maps)
      } else {
        reject(new Error('Google Maps SDK 回调缺少 maps 对象'))
      }
    }
    document.head.appendChild(script)
  })
  return loadingPromise
}
